// src/pages/Dashboard2.tsx - Compact dashboard with inline progress

import React, { useState, useEffect, useRef } from 'react';
import { Card, Upload, Button, Input, message, Steps, Tag, Modal, Skeleton, Alert, Space, Select, Progress } from 'antd';
import {
  InboxOutlined,
  PlayCircleOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined,
  PauseCircleOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  ShareAltOutlined,
  AudioOutlined,
  VideoCameraOutlined,
  PictureOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useGetIdentity } from '@refinedev/core';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../utils/firebase';
import { getCurrentUserToken, clearImpersonationCache, authenticatedFetch } from '../providers/authProvider';
import { useNavigate } from 'react-router-dom';
import VideoPlayer from '../components/VideoPlayer';
import { ShareModal } from '../components/ShareModal';
import { PDFViewerModal } from '../components/pdf-viewer';
import { getReviewLimit, hasActivePlan } from '../utils/planLimits';

const { Dragger } = Upload;
const { TextArea } = Input;

interface TaskStatus {
  status: 'PENDING' | 'PROCESSING' | 'RENDERING' | 'COMPLETED' | 'APPROVED' | 'ERROR';
  script_text?: string;
  error?: string;
  video_url?: string;
  report_data?: any;
  processed_data?: any;
}

interface JobData {
  job_id: string;
  status: string;
  script_review_enabled: boolean;
  generation_type?: 'script_only' | 'video';
  original_filename?: string;
  created_at?: any;
  tasks: {
    create_script: TaskStatus;
    extract_images: TaskStatus;
    create_narration: TaskStatus;
    create_video: TaskStatus;
  };
}

interface UploadTask {
  task: any;
  progress: number;
  state: 'running' | 'paused' | 'success' | 'canceled' | 'error';
  error?: string;
}

type TaskKey = 'create_script' | 'extract_images' | 'create_narration' | 'create_video';

const STAGE_META: { [key in TaskKey]: { label: string; icon: React.ReactNode; activeText: string; doneText: string } } = {
  create_script: {
    label: 'Script',
    icon: <EditOutlined />,
    activeText: 'Analyzing inspection report and generating narration script...',
    doneText: 'Script generated successfully.',
  },
  extract_images: {
    label: 'Images',
    icon: <PictureOutlined />,
    activeText: 'Extracting and processing images from the report...',
    doneText: 'Images extracted successfully.',
  },
  create_narration: {
    label: 'Narration',
    icon: <AudioOutlined />,
    activeText: 'Converting script to audio narration...',
    doneText: 'Narration generated successfully.',
  },
  create_video: {
    label: 'Video',
    icon: <VideoCameraOutlined />,
    activeText: 'Assembling final video presentation...',
    doneText: 'Video rendering complete!',
  },
};

const TASK_ORDER: TaskKey[] = ['create_script', 'extract_images', 'create_narration', 'create_video'];

const Dashboard2: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'video' | 'script'>('video');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentJob, setCurrentJob] = useState<JobData | null>(null);
  const [scriptReviewEnabled, setScriptReviewEnabled] = useState(false);
  const [extraInstructions, setExtraInstructions] = useState('');
  const [language, setLanguage] = useState('en');
  const [showScriptModal, setShowScriptModal] = useState(false);
  const [editableScript, setEditableScript] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploadTask, setUploadTask] = useState<UploadTask | null>(null);
  const { data: user, refetch } = useGetIdentity<any>();

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [isDashboardReady, setIsDashboardReady] = useState(false);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [pdfViewerState, setPdfViewerState] = useState<{
    visible: boolean;
    pdfUrl: string;
    documentId: string;
    documentTitle: string;
  }>({ visible: false, pdfUrl: '', documentId: '', documentTitle: '' });

  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setIsDashboardReady(true), 50);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (user?.language) setLanguage(user.language);
  }, [user]);

  // Firestore job listener
  useEffect(() => {
    if (!currentJob?.job_id) return;

    const unsubscribe = onSnapshot(
      doc(db, 'jobs', currentJob.job_id),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data() as JobData;
          setCurrentJob(prev => ({ ...prev, ...data }));

          if (data.script_review_enabled &&
            data.tasks.create_script.status === 'COMPLETED' &&
            data.tasks.create_script.script_text &&
            !showScriptModal) {
            setEditableScript(data.tasks.create_script.script_text);
            setShowScriptModal(true);
          }

          if (data.status === 'COMPLETED' && data.tasks.create_video.video_url) {
            message.success('Video generation completed!');
            clearImpersonationCache();
            refetch();
          }

          if (data.status === 'ERROR' || data.tasks.create_script.status === 'ERROR') {
            clearImpersonationCache();
            refetch();
          }
        }
      },
      (error) => console.error('Error listening to job updates:', error)
    );

    return () => unsubscribe();
  }, [currentJob?.job_id, showScriptModal, refetch]);

  // --- Helpers ---
  const getMaxFileSize = () => (user?.plan || 'free') === 'free' ? 25 * 1024 * 1024 : 100 * 1024 * 1024;
  const getMaxFileSizeMB = () => (user?.plan || 'free') === 'free' ? 25 : 100;
  const getVideoLimit = () => getReviewLimit(user?.plan);
  // Under overage billing there is no hard cap — generation is gated only on
  // holding an active paid plan.
  const isAtVideoLimit = () => !hasActivePlan(user?.plan);
  const isAtScriptLimit = () => isAtVideoLimit();
  const isJobProcessing = () => currentJob && (currentJob.status === 'PROCESSING' || currentJob.status === 'PENDING');
  const isJobComplete = () => currentJob?.status === 'COMPLETED' && currentJob?.tasks?.create_video?.video_url;
  const isJobError = () => currentJob?.status === 'ERROR';
  const isScriptOnlyComplete = () => currentJob?.generation_type === 'script_only' && currentJob?.tasks?.create_script?.status === 'COMPLETED';

  const getDisplayTitle = (): string => {
    if (!currentJob) return 'Inspection Video';
    if ((currentJob as any).extracted_address) return (currentJob as any).extracted_address;
    if (currentJob.original_filename) return currentJob.original_filename.replace('.pdf', '');
    return `Video-${currentJob.job_id.substring(0, 8)}`;
  };

  const getStepStatus = (taskKey: TaskKey): 'wait' | 'process' | 'finish' | 'error' => {
    if (!currentJob) return 'wait';
    const task = currentJob.tasks[taskKey];
    if (!task) return 'wait';
    switch (task.status) {
      case 'COMPLETED':
      case 'APPROVED': return 'finish';
      case 'PROCESSING':
      case 'RENDERING': return 'process';
      case 'ERROR': return 'error';
      default: return 'wait';
    }
  };

  // Find the currently active (processing) stage
  const getActiveStage = (): TaskKey | null => {
    if (!currentJob) return null;
    for (const key of TASK_ORDER) {
      const s = currentJob.tasks[key]?.status;
      if (s === 'PROCESSING' || s === 'RENDERING') return key;
    }
    return null;
  };

  // Find the latest completed stage
  const getLatestCompletedStage = (): TaskKey | null => {
    if (!currentJob) return null;
    let latest: TaskKey | null = null;
    for (const key of TASK_ORDER) {
      const s = currentJob.tasks[key]?.status;
      if (s === 'COMPLETED' || s === 'APPROVED') latest = key;
    }
    return latest;
  };

  // Find the first error stage
  const getErrorStage = (): TaskKey | null => {
    if (!currentJob) return null;
    for (const key of TASK_ORDER) {
      if (currentJob.tasks[key]?.status === 'ERROR') return key;
    }
    return null;
  };

  // Get current step index for the Steps component
  const getCurrentStepIndex = (): number => {
    if (!currentJob) return 0;
    for (let i = TASK_ORDER.length - 1; i >= 0; i--) {
      const s = currentJob.tasks[TASK_ORDER[i]]?.status;
      if (s === 'COMPLETED' || s === 'APPROVED') return i + 1;
      if (s === 'PROCESSING' || s === 'RENDERING') return i;
      if (s === 'ERROR') return i;
    }
    return 0;
  };

  // --- Upload handler ---
  const handleFirebaseUpload = async (uploadFile: File, type: 'script' | 'video') => {
    const maxSize = getMaxFileSize();
    if (uploadFile.size > maxSize) {
      message.error(`File too large. Maximum size: ${getMaxFileSizeMB()}MB`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const jobId = `job_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const impersonateUid = localStorage.getItem('impersonateUid');
      const userId = impersonateUid || user?.uid || user?.id || auth.currentUser?.uid;

      if (!userId) {
        message.error('User not authenticated');
        setUploading(false);
        return;
      }

      const storageRef = ref(storage, `uploads/${userId}/${timestamp}_${uploadFile.name}`);
      const uploadTaskInstance = uploadBytesResumable(storageRef, uploadFile, {
        contentType: 'application/pdf',
        customMetadata: {
          userId: user?.uid || '',
          jobId: jobId,
          originalName: uploadFile.name,
          generationType: type,
          uploadedAt: new Date().toISOString()
        }
      });

      setUploadTask({ task: uploadTaskInstance, progress: 0, state: 'running' });

      uploadTaskInstance.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
          setUploadTask(prev => prev ? { ...prev, progress, state: snapshot.state === 'paused' ? 'paused' : 'running' } : null);
        },
        (error) => {
          console.error('Upload error:', error);
          setUploadTask(prev => prev ? { ...prev, state: 'error', error: error.message } : null);
          setUploading(false);
          if (error.code === 'storage/unauthorized') message.error('Unauthorized. Please log in again.');
          else if (error.code === 'storage/canceled') message.info('Upload canceled');
          else message.error('Upload failed: ' + error.message);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTaskInstance.snapshot.ref);
          const jobRef = doc(db, 'jobs', jobId);

          const jobData: any = {
            uid: userId,
            planType: user?.plan || user?.planType || 'free',
            status: 'PENDING',
            created_at: serverTimestamp(),
            storage_path: uploadTaskInstance.snapshot.ref.fullPath,
            file_url: downloadURL,
            original_filename: uploadFile.name,
            script_review_enabled: scriptReviewEnabled,
            generation_type: type === 'script' ? 'script_only' : 'video',
            skip_video_creation: false,
            extra_instructions: extraInstructions,
            tasks: {
              create_script: { status: 'PENDING' },
              extract_images: { status: 'PENDING' },
              create_narration: { status: 'PENDING' },
              create_video: { status: 'PENDING' }
            }
          };

          const isPaidPlan = user?.plan && user.plan !== 'free';
          if (isPaidPlan && user?.companyLogo) jobData.company_logo = user.companyLogo;
          if (isPaidPlan && user?.company) jobData.company_name = user.company;

          await setDoc(jobRef, jobData);

          const token = await getCurrentUserToken();
          const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/process-upload`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              jobId, storageUrl: downloadURL,
              storagePath: uploadTaskInstance.snapshot.ref.fullPath,
              generationType: type, extraInstructions, scriptReviewEnabled, language
            })
          });

          if (response.ok) {
            setCurrentJob({
              job_id: jobId, status: 'PROCESSING',
              script_review_enabled: scriptReviewEnabled,
              generation_type: type === 'script' ? 'script_only' : 'video',
              original_filename: uploadFile.name,
              tasks: {
                create_script: { status: 'PROCESSING' },
                extract_images: { status: 'PENDING' },
                create_narration: { status: 'PENDING' },
                create_video: { status: 'PENDING' }
              }
            });
            clearImpersonationCache();
            refetch();
            setUploadTask(prev => prev ? { ...prev, state: 'success' } : null);
            message.success('File uploaded - processing started!');
          } else {
            throw new Error('Failed to start processing');
          }

          setUploading(false);
          setUploadProgress(0);
          setFile(null);
          setUploadTask(null);
        }
      );
    } catch (error) {
      console.error('Upload error:', error);
      message.error('Upload failed');
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handlePauseResume = () => {
    if (!uploadTask?.task) return;
    if (uploadTask.state === 'paused') uploadTask.task.resume();
    else if (uploadTask.state === 'running') uploadTask.task.pause();
  };

  const handleCancelUpload = () => {
    if (!uploadTask?.task) return;
    uploadTask.task.cancel();
    setUploadTask(null);
    setUploading(false);
    setUploadProgress(0);
  };

  const handleScriptApproval = async () => {
    if (!currentJob?.job_id) return;
    try {
      const token = await getCurrentUserToken();
      if (!token) { message.error('Authentication required.'); return; }
      const impersonateUid = localStorage.getItem('impersonateUid');
      const body: any = { job_id: currentJob.job_id, script_text: editableScript };
      if (impersonateUid) body.impersonate = impersonateUid;

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/approve-script`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        message.success('Script approved! Continuing...');
        setShowScriptModal(false);
      } else {
        const result = await response.json();
        message.error(result.error || 'Failed to approve script');
      }
    } catch (error) {
      console.error('Script approval error:', error);
      message.error('Failed to approve script');
    }
  };

  const handleScriptCancel = async () => {
    if (!currentJob?.job_id) return;
    try {
      const token = await getCurrentUserToken();
      if (!token) { message.error('Authentication required.'); return; }
      const impersonateUid = localStorage.getItem('impersonateUid');
      const body: any = { job_id: currentJob.job_id, reason: 'User cancelled during script review' };
      if (impersonateUid) body.impersonate = impersonateUid;

      await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/cancel-job`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      });
    } catch (_) { /* noop */ }
    setShowScriptModal(false);
    setCurrentJob(null);
    message.info('Job cancelled');
  };

  const handleViewPDF = async () => {
    if (!currentJob) return;
    try {
      const response = await authenticatedFetch(`/proxy-pdf/${currentJob.job_id}`);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to load PDF' }));
        throw new Error(error.error || 'Failed to load PDF');
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPdfViewerState({ visible: true, pdfUrl: blobUrl, documentId: currentJob.job_id, documentTitle: getDisplayTitle() });
    } catch (error: any) {
      console.error('Error getting PDF:', error);
      message.error(error.message || 'Failed to load PDF');
    }
  };

  const handleDownloadVideo = () => {
    if (!currentJob) return;
    const videoUrl = currentJob.tasks?.create_video?.video_url;
    if (videoUrl) {
      const link = document.createElement('a');
      link.href = videoUrl;
      link.download = `${currentJob.original_filename?.replace('.pdf', '')}_video.mp4` || 'inspection_video.mp4';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success('Video download started!');
    } else {
      message.error('Video not available for download');
    }
  };

  const handleShare = () => {
    if (!currentJob) return;
    if (!currentJob.tasks?.create_video?.video_url) {
      message.error('Video is not ready to share yet');
      return;
    }
    setShareModalVisible(true);
  };

  const handleReset = () => {
    setCurrentJob(null);
    setFile(null);
    setExtraInstructions('');
    setScriptReviewEnabled(false);
  };

  // --- Upload area props ---
  const uploadProps = {
    name: 'file',
    multiple: false,
    accept: '.pdf',
    disabled: !!isJobProcessing(),
    customRequest: ({ onSuccess }: any) => onSuccess("ok"),
    beforeUpload: (uploadedFile: File) => {
      if (isJobProcessing()) {
        message.warning('Please wait for the current job to complete');
        return false;
      }
      const maxSize = getMaxFileSize();
      if (uploadedFile.size > maxSize) {
        message.error(`File too large. Maximum size: ${getMaxFileSizeMB()}MB`);
        return false;
      }
      setFile(uploadedFile);
      return false;
    },
    onChange: (info: any) => { if (info.file.status === 'removed') setFile(null); },
    showUploadList: false,
  };

  // --- Loading skeleton ---
  if (!isDashboardReady) {
    return (
      <div style={{ maxWidth: 960, margin: '0 auto', padding: isMobile ? 12 : 24 }}>
        <Card><Skeleton active paragraph={{ rows: 4 }} /></Card>
      </div>
    );
  }

  // ===========================
  // RENDER: Processing / Complete state
  // ===========================
  if (currentJob) {
    const activeStage = getActiveStage();
    const errorStage = getErrorStage();
    const latestCompleted = getLatestCompletedStage();
    const focusStage = activeStage || errorStage || latestCompleted;
    const isScriptOnly = currentJob.generation_type === 'script_only';
    const stepsToShow = isScriptOnly ? ['create_script'] as TaskKey[] : TASK_ORDER;

    return (
      <div style={{ maxWidth: 960, margin: '0 auto', padding: isMobile ? 12 : 24 }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
              {isJobComplete() ? 'Video Ready' : isScriptOnlyComplete() ? 'Report Ready' : isJobError() ? 'Processing Error' : 'Processing'}
            </h2>
            <span style={{ fontSize: 13, color: '#888' }}>{currentJob.original_filename}</span>
          </div>
          {(isJobComplete() || isScriptOnlyComplete() || isJobError()) && (
            <Button onClick={handleReset} size="small">New Report</Button>
          )}
        </div>

        {/* Horizontal progress stepper */}
        <Card bodyStyle={{ padding: isMobile ? '12px 8px' : '16px 24px' }} style={{ marginBottom: 16 }}>
          <Steps
            current={getCurrentStepIndex()}
            size="small"
            direction={isMobile ? 'vertical' : 'horizontal'}
            items={stepsToShow.map((key) => {
              const st = getStepStatus(key);
              return {
                title: STAGE_META[key].label,
                status: st,
                icon: st === 'process' ? <LoadingOutlined /> :
                  st === 'finish' ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> :
                    st === 'error' ? <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} /> :
                      STAGE_META[key].icon,
              };
            })}
          />
        </Card>

        {/* Dynamic stage card */}
        <Card bodyStyle={{ padding: isMobile ? 16 : 24 }}>
          {/* --- Video complete --- */}
          {isJobComplete() && (
            <>
              <VideoPlayer
                videoUrl={currentJob.tasks.create_video.video_url!}
                title={getDisplayTitle()}
                createdAt={currentJob.created_at}
                jobId={currentJob.job_id}
                hideTitle={true}
              />
              <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                <Button type="primary" icon={<ShareAltOutlined />} onClick={handleShare}>Share Report</Button>
                <Button icon={<DownloadOutlined />} onClick={handleDownloadVideo}>Download</Button>
                <Button icon={<FilePdfOutlined />} onClick={handleViewPDF}>View PDF</Button>
                <Button onClick={() => navigate('/videos')}>All Videos</Button>
              </div>
            </>
          )}

          {/* --- Script-only complete --- */}
          {isScriptOnlyComplete() && !isJobComplete() && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
              <h3 style={{ marginBottom: 8 }}>Text Report Ready!</h3>
              <p style={{ color: '#666', marginBottom: 16 }}>Your inspection text report has been generated.</p>
              <Button type="primary" onClick={() => navigate(`/scripts?view=${currentJob.job_id}`)}>
                View Text Report
              </Button>
            </div>
          )}

          {/* --- Error state --- */}
          {isJobError() && errorStage && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <ExclamationCircleOutlined style={{ fontSize: 48, color: '#ff4d4f', marginBottom: 16 }} />
              <h3 style={{ marginBottom: 8, color: '#ff4d4f' }}>Processing Error</h3>
              <p style={{ color: '#666', marginBottom: 8 }}>
                Failed during: {STAGE_META[errorStage].label}
              </p>
              {currentJob.tasks[errorStage]?.error && (
                <Alert
                  type="error"
                  message={currentJob.tasks[errorStage].error}
                  style={{ textAlign: 'left', marginBottom: 16 }}
                />
              )}
              <Button onClick={handleReset}>Try Again</Button>
            </div>
          )}

          {/* --- In-progress state --- */}
          {!isJobComplete() && !isScriptOnlyComplete() && !isJobError() && (() => {
            // Determine what to show:
            // 1. If a stage is actively PROCESSING/RENDERING -> show that stage with spinner
            // 2. If nothing active but stages are completed (waiting for next) -> show completed info + waiting
            // 3. If nothing started yet -> show "Starting..."
            const isActivelyProcessing = !!activeStage;

            if (isActivelyProcessing && activeStage) {
              return (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <LoadingOutlined style={{ fontSize: 40, color: '#1890ff', marginBottom: 16 }} />
                  <h3 style={{ marginBottom: 8 }}>
                    {STAGE_META[activeStage].label}
                  </h3>
                  <p style={{ color: '#666', maxWidth: 400, margin: '0 auto' }}>
                    {STAGE_META[activeStage].activeText}
                  </p>
                </div>
              );
            }

            if (latestCompleted) {
              // Something finished but next stage hasn't started yet (gap between stages)
              // Find what's coming next
              const completedIdx = TASK_ORDER.indexOf(latestCompleted);
              const nextStage = completedIdx < TASK_ORDER.length - 1 ? TASK_ORDER[completedIdx + 1] : null;
              return (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <CheckCircleOutlined style={{ fontSize: 40, color: '#52c41a', marginBottom: 16 }} />
                  <h3 style={{ marginBottom: 8, color: '#52c41a' }}>
                    {STAGE_META[latestCompleted].label} Complete
                  </h3>
                  <p style={{ color: '#666', maxWidth: 400, margin: '0 auto', marginBottom: 12 }}>
                    {STAGE_META[latestCompleted].doneText}
                  </p>
                  {nextStage && (
                    <p style={{ color: '#999', fontSize: 13 }}>
                      <LoadingOutlined style={{ marginRight: 6 }} />
                      Preparing {STAGE_META[nextStage].label.toLowerCase()}...
                    </p>
                  )}
                </div>
              );
            }

            // Nothing started yet
            return (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <LoadingOutlined style={{ fontSize: 40, color: '#1890ff', marginBottom: 16 }} />
                <h3 style={{ marginBottom: 8 }}>Starting...</h3>
                <p style={{ color: '#666', maxWidth: 400, margin: '0 auto' }}>
                  Initializing pipeline...
                </p>
              </div>
            );
          })()}
        </Card>

        {/* Script review modal */}
        <Modal
          title="Review Generated Script"
          open={showScriptModal}
          onCancel={handleScriptCancel}
          width={isMobile ? '95%' : 800}
          style={{ top: isMobile ? 20 : undefined }}
          footer={[
            <Button key="cancel" onClick={handleScriptCancel}>Cancel</Button>,
            <Button key="approve" type="primary" onClick={handleScriptApproval}>Approve Script and Continue</Button>,
          ]}
        >
          <p style={{ color: '#666', marginBottom: 8 }}>
            Review and edit the script below, then click approve to continue processing.
          </p>
          <p style={{ fontSize: 13, color: '#f59e0b', marginBottom: 12 }}>
            Note: Do not change or add section headers or alter bracketed sections.
          </p>
          <TextArea
            value={editableScript}
            onChange={(e) => setEditableScript(e.target.value)}
            rows={isMobile ? 10 : 15}
            className="font-mono text-sm"
          />
        </Modal>

        <PDFViewerModal
          visible={pdfViewerState.visible}
          pdfUrl={pdfViewerState.pdfUrl}
          documentId={pdfViewerState.documentId}
          documentTitle={pdfViewerState.documentTitle}
          onClose={() => setPdfViewerState({ visible: false, pdfUrl: '', documentId: '', documentTitle: '' })}
        />

        {currentJob && (
          <ShareModal
            visible={shareModalVisible}
            onClose={() => setShareModalVisible(false)}
            jobId={currentJob.job_id}
            videoTitle={getDisplayTitle()}
          />
        )}
      </div>
    );
  }

  // ===========================
  // RENDER: Upload state (no job)
  // ===========================
  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: isMobile ? 12 : 24 }}>
      <Card bodyStyle={{ padding: isMobile ? 12 : 24 }}>
        {/* Tab-like toggle at top */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <Button
            type={activeTab === 'video' ? 'primary' : 'default'}
            icon={<PlayCircleOutlined />}
            onClick={() => setActiveTab('video')}
          >
            {isMobile ? 'Video' : 'Video Review'}
          </Button>
          <Button
            type={activeTab === 'script' ? 'primary' : 'default'}
            icon={<FileTextOutlined />}
            onClick={() => setActiveTab('script')}
          >
            {isMobile ? 'Text' : 'Text Review'}
          </Button>
          {hasActivePlan(user?.plan) && activeTab === 'video' && (
            <span style={{ marginLeft: 'auto', fontSize: 13, color: '#888', alignSelf: 'center' }}>
              {user?.usage?.videosThisMonth || 0}/{getVideoLimit()} included this month
            </span>
          )}
        </div>

        {/* Subscription required */}
        {(isAtVideoLimit() || isAtScriptLimit()) && (
          <Alert
            message="Subscription Required"
            description="Choose a plan to start creating reviews. Your first month's base is free."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            action={<Button type="primary" size="small" onClick={() => navigate('/billing')}>Choose a Plan</Button>}
          />
        )}

        {/* Upload area */}
        {!(activeTab === 'video' && isAtVideoLimit()) && !(activeTab === 'script' && isAtScriptLimit()) && (
          <>
            {/* Dragger / file selected / uploading */}
            {!file && !uploadTask ? (
              <Dragger {...uploadProps} style={{ padding: isMobile ? '16px' : '32px' }}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined style={{ fontSize: isMobile ? 40 : 56 }} />
                </p>
                <p className="ant-upload-text" style={{ fontSize: isMobile ? 14 : 16 }}>
                  Click or drag PDF inspection report to upload
                </p>
                <p className="ant-upload-hint" style={{ fontSize: isMobile ? 12 : 14 }}>
                  Maximum file size: {getMaxFileSizeMB()}MB
                </p>
              </Dragger>
            ) : uploadTask ? (
              <div style={{
                padding: 20, border: '2px dashed #d9d9d9', borderRadius: 8,
                backgroundColor: '#fafafa', textAlign: 'center'
              }}>
                <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 8 }}>
                  {uploadTask.state === 'paused' ? 'Upload Paused' : 'Uploading...'} {file?.name}
                </p>
                <Progress percent={Math.round(uploadTask.progress)} status={uploadTask.state === 'error' ? 'exception' : 'active'} />
                <Space style={{ marginTop: 12 }}>
                  <Button
                    icon={uploadTask.state === 'paused' ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
                    onClick={handlePauseResume}
                    size="small"
                  >
                    {uploadTask.state === 'paused' ? 'Resume' : 'Pause'}
                  </Button>
                  <Button danger icon={<DeleteOutlined />} onClick={handleCancelUpload} size="small">Cancel</Button>
                </Space>
                {uploadTask.error && <p style={{ color: '#ff4d4f', marginTop: 8 }}>{uploadTask.error}</p>}
              </div>
            ) : (
              <div style={{
                padding: 20, border: '2px dashed #91d5ff', borderRadius: 8,
                backgroundColor: '#e6f7ff', textAlign: 'center'
              }}>
                <FileTextOutlined style={{ fontSize: 36, color: '#1890ff', marginBottom: 8 }} />
                <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>{file?.name}</p>
                <Button danger size="small" onClick={() => setFile(null)}>Remove</Button>
              </div>
            )}

            {/* Options row - compact inline layout */}
            <div style={{
              marginTop: 16, padding: '12px 16px',
              backgroundColor: '#fafafa', borderRadius: 8,
              display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start'
            }}>
              {activeTab === 'video' && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, whiteSpace: 'nowrap' }}>
                  <input
                    type="checkbox"
                    checked={scriptReviewEnabled}
                    onChange={(e) => setScriptReviewEnabled(e.target.checked)}
                  />
                  Review script first
                </label>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 14, whiteSpace: 'nowrap' }}>Language:</span>
                <Select value={language} onChange={setLanguage} size="small" style={{ width: 110 }}>
                  <Select.Option value="en">English</Select.Option>
                  <Select.Option value="es">Spanish</Select.Option>
                </Select>
              </div>
              <div style={{ flex: '1 1 200px', minWidth: 200 }}>
                <TextArea
                  value={extraInstructions}
                  onChange={(e) => setExtraInstructions(e.target.value)}
                  placeholder="Extra instructions for the AI (optional)"
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  style={{ fontSize: 13 }}
                />
              </div>
            </div>

            {/* Generate button */}
            <div style={{ marginTop: 16 }}>
              <Button
                type="primary"
                onClick={() => file && handleFirebaseUpload(file, activeTab)}
                loading={uploading}
                disabled={!file}
                size={isMobile ? 'middle' : 'large'}
                block={isMobile}
              >
                {activeTab === 'video' ? 'Generate Video' : 'Generate Text Report'}
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default Dashboard2;
